import { FC, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import * as yup from 'yup';
import useFormHandle from '../../hooks/use-form-handle.hook';
import { createUseStyles } from 'react-jss';
import InputControl from '../../components/form/input-control';
import Button from '../../components/button';
import { AuthMethod, execAuthMethod } from '../../lib/services/auth.service';
import InlineToast from '../../components/inline-toast';
import Text from '../../components/text';
import useApp from '../../hooks/use-app.hook';
import { useNavigate } from '@tanstack/react-router';
import usePrior from '../../hooks/use-prior.hook';

const validationSchema = yup.object({
  password: yup.string().required('Password is required'),
});

const useStyles = createUseStyles({
  footer: {
    marginTop: 10,
    marginBottom: 10,
  },
});

const initialValues = {
  password: '',
  rememberMe: [],
};

interface Props {
  authMethod: AuthMethod;
}

const EmailPasswordForm: FC<Props> = ({ authMethod }) => {
  const styles = useStyles();
  const [loginError, setLoginError] = useState('');
  const { signedIn, reloadAuth } = useApp();
  const navigate = useNavigate();

  const formHandle = useFormHandle({
    initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setLoginError('');
      execAuthMethod({
        ...authMethod,
        password: values.password,
      }).then((response) => {
        setSubmitting(false);

        if (typeof response === 'string') {
          setLoginError(response);
          return;
        }
        reloadAuth();
      });
    },
    validateOnChange: false,
  });

  const { handleSubmit, isSubmitting } = formHandle;
  const handleOnEnter = () => handleSubmit();
  const hasError = Boolean(loginError);

  const priorSignedIn = usePrior(signedIn);
  useEffect(() => {
    if (signedIn && !priorSignedIn) {
      navigate({ to: '/home' });
    }
  }, [navigate, signedIn, priorSignedIn]);

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Text center color="grey" dotdot>
          {authMethod.email}
        </Text>
        <InputControl
          name="password"
          label="Password"
          type="password"
          formHandle={formHandle}
          onEnter={handleOnEnter}
          autoFocus
        />
        <Box className={styles.footer}>
          <Button label="Sign In" fullWidth disabled={isSubmitting} onClick={handleSubmit} />
        </Box>
      </form>
      {hasError && (
        <Box mt={2}>
          <InlineToast level="error" message={loginError} />
        </Box>
      )}
    </>
  );
};

export default EmailPasswordForm;
